import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {StickyPopover} from "asax-common";
import {SkyLayer} from "../aladin-sky-layers-options/aladin-sky-layers-options.component";
import { Observable } from 'rxjs';
import { AladinSkyLayersService } from '../aladin-sky-layers.service';

const valueBetweenRanges = 10000;
const sliderThumbWidth = 16;
const sliderOpacityMouseLeave = 0.3;

@Component({
  selector: 'asax-sky-view-aladin-sky-layers-slider',
  templateUrl: './aladin-sky-layers-slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host {
      position: absolute;
      bottom: 5px;
      z-index: 9;
      left: calc((100% - 200px) / 2);
      width: 200px;
      font-size: 8px !important;
    }
      
    ::ng-deep .sky-layers-slider-popover .popover-body {
      padding: 0 !important;
      border: 0 !important;
    }

    ::ng-deep .sky-layers-slider-popover .accordion-body {
      padding: 0 !important;
      border: 0 !important;
    }
  `]
})
export class AladinSkyLayersSliderComponent {
  @ViewChild('stickyPopover', {static: true}) stickyPopover: StickyPopover;
  @ViewChild('sliderBar', {static: true}) slideBar: ElementRef;

  @Input() skyLayers: SkyLayer[];

  @Output() outBaseImageForAladin = new EventEmitter<SkyLayer>();
  @Output() outOverlayImageForAladin = new EventEmitter<SkyLayer>();
  @Output() outAlphaForAladin = new EventEmitter<number>();

  @Output() outBaseImageForProgress = new EventEmitter<SkyLayer>();
  @Output() outOverlayImageForProgress = new EventEmitter<SkyLayer>();
  @Output() outAlphaForProgress = new EventEmitter<number>();

  @Output() outIndexActiveSkyLayer = new EventEmitter<number>();

  currentSliderValue: number;
  maximumSliderValue: number;
  marginLeftPopover: number;

  baseSkyIndex: number;
  overlaySkyIndex: number;
  alpha: number;

  sliderOpacity = sliderOpacityMouseLeave;
  fadeOutSliderTimeOut: NodeJS.Timeout;
  optionsViewStyle: Observable<string>;
  optionsViewPlacement: Observable<string>;

  popoverCloseDelay = 300;

  constructor(private readonly ref: ChangeDetectorRef,
    private readonly aladinLayersService: AladinSkyLayersService
  ) {
    this.optionsViewStyle = this.aladinLayersService.getSkyLayersStyle();
    this.optionsViewPlacement = this.aladinLayersService.getSkyLayersPlacement();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.skyLayers && this.skyLayers !== undefined) {
      this.onSkyLayers();
    }
  }

  onSkyLayers(): void {
    this.setMaximumSliderValue();

    this.setBaseSkyIndexAndOverlaySkyIndexAndAlphaAndCurrentSliderValue();

    this.setLeftMarginPopover();

    this.emitBaseImageForAladin();
    this.emitOverlayImageForAladin();
    this.emitAlphaForAladin();

    this.emitBaseImageForProgress();
    this.emitOverlayImageForProgress();
    this.emitAlphaForProgress();
  }

  private setMaximumSliderValue(): void {
    this.maximumSliderValue = (this.skyLayers.length - 1) * valueBetweenRanges;
  }

  private setLeftMarginPopover() {
    this.marginLeftPopover = (this.currentSliderValue * (this.slideBar?.nativeElement?.clientWidth - sliderThumbWidth)) / this.maximumSliderValue + (sliderThumbWidth / 2);
  }

  private setBaseSkyIndexAndOverlaySkyIndexAndAlphaAndCurrentSliderValue(): void {
    const activeSkyIndex = this.skyLayers.findIndex(overlayImage => overlayImage.active);
    const isActiveSkyIndexLastSky = (activeSkyIndex === this.skyLayers.length - 1);

    if (activeSkyIndex === 0) {
      this.overlaySkyIndex = 0;
      this.baseSkyIndex = this.overlaySkyIndex + 1;

      this.alpha = 1;

      this.currentSliderValue = 0;
    } else if (isActiveSkyIndexLastSky) {
      this.overlaySkyIndex = this.skyLayers.length - 1;
      this.baseSkyIndex = this.overlaySkyIndex - 1;

      this.alpha = 1;

      this.currentSliderValue = this.maximumSliderValue;
    } else {
      this.baseSkyIndex = activeSkyIndex;
      this.overlaySkyIndex = this.baseSkyIndex + 1;

      this.alpha = 0;

      this.currentSliderValue = this.baseSkyIndex * valueBetweenRanges;
    }
  }

  onSliderEvent(): void {
    this.setLeftMarginPopover();

    this.setBaseImageIndexWhenSliderEvent();
    this.setOverlayImageIndexWhenSliderEvent();
    this.setAlphaWhenSliderEvent();

    this.emitBaseImageForAladin();
    this.emitOverlayImageForAladin();
    this.emitAlphaForAladin();

    this.emitBaseImageForProgress();
    this.emitOverlayImageForProgress();
    this.emitAlphaForProgress();

    this.emitIndexActiveSkyLayer();
  }

  private setBaseImageIndexWhenSliderEvent(): void {
    if (this.currentSliderValue < this.maximumSliderValue) {
      this.baseSkyIndex = Math.floor(Math.floor(this.currentSliderValue / valueBetweenRanges) / 2.0) * 2.0 + 1;
    }
  }

  private setOverlayImageIndexWhenSliderEvent(): void {
    if (this.currentSliderValue < this.maximumSliderValue) {
      this.overlaySkyIndex = Math.floor(Math.floor(this.currentSliderValue / valueBetweenRanges + 1.0) / 2.0) * 2.0;
    }
  }

  private setAlphaWhenSliderEvent(): void {
    if (this.currentSliderValue < this.maximumSliderValue) {
      this.alpha = (this.currentSliderValue / valueBetweenRanges) - Math.floor(this.currentSliderValue / valueBetweenRanges);

      if ((this.overlaySkyIndex - this.baseSkyIndex) < 0) {
        this.alpha = 1 - this.alpha;
      }
    }
  }

  private emitBaseImageForAladin(): void {
    let baseSky = this.skyLayers[this.baseSkyIndex];
    this.outBaseImageForAladin.emit(baseSky);
  }

  private emitOverlayImageForAladin(): void {
    let overlaySky = this.skyLayers[this.overlaySkyIndex];
    this.outOverlayImageForAladin.emit(overlaySky);
  }

  private emitAlphaForAladin(): void {
    this.outAlphaForAladin.emit(this.alpha);
  }

  private emitBaseImageForProgress(): void {
    if ((this.overlaySkyIndex - this.baseSkyIndex) < 0) {
      this.outBaseImageForProgress.emit(this.skyLayers[this.overlaySkyIndex]);
    } else {
      this.outBaseImageForProgress.emit(this.skyLayers[this.baseSkyIndex]);
    }
  }

  private emitOverlayImageForProgress(): void {
    if ((this.overlaySkyIndex - this.baseSkyIndex) < 0) {
      this.outOverlayImageForProgress.emit(this.skyLayers[this.baseSkyIndex]);
    } else {
      this.outOverlayImageForProgress.emit(this.skyLayers[this.overlaySkyIndex]);
    }
  }

  private emitAlphaForProgress(): void {
    if ((this.overlaySkyIndex - this.baseSkyIndex) < 0) {
      this.outAlphaForProgress.emit(1 - this.alpha);
    } else {
      this.outAlphaForProgress.emit(this.alpha);
    }
  }

  private emitIndexActiveSkyLayer(): void {
    if ((this.overlaySkyIndex - this.baseSkyIndex) < 0) {
      this.outIndexActiveSkyLayer.emit(this.overlaySkyIndex);
    } else {
      this.outIndexActiveSkyLayer.emit(this.baseSkyIndex);
    }
  }

  fadeInSlider(): void {
    clearTimeout(this.fadeOutSliderTimeOut);
    this.sliderOpacity = 1;
    this.ref.markForCheck();
  }

  fadeOutSlider(): void {
    this.sliderOpacity = sliderOpacityMouseLeave;
    this.ref.markForCheck();
  }

  onMouseHover(): void {
    this.fadeInSlider();
    this.setLeftMarginPopover();
    this.stickyPopover.open();
  }

  onMouseLeave(): void {
    this.fadeOutSliderTimeOut = setTimeout(() => this.fadeOutSlider(), this.popoverCloseDelay);
    this.stickyPopover.closeWithTimeout();
  }
}
